/**
 * V5 — "Luminous"
 * Animated mesh gradient background. Elements that glow on scroll-trigger.
 * Gradient-filled text. Floating light orbs. Glass cards with colored borders.
 * Fonts: Instrument Serif (display) + DM Sans (body)
 */
import { useRef, useLayoutEffect, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, FolderOpen, Sparkles, Users, Zap, Shield, Wand2 } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const SERIF = "'Instrument Serif', serif";
const SANS = "'DM Sans', sans-serif";
const TEAL = '#2EC4B6';
const CORAL = '#ef5228';
const VIOLET = '#7c3aed';

export function LandingV5() {
  const orbsRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handler = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Hero stagger
      gsap.fromTo('.v5l-hero', { y: 30, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.12, duration: 0.8, ease: 'power3.out' });

      // Floating orbs
      if (orbsRef.current) {
        Array.from(orbsRef.current.children).forEach((orb, i) => {
          gsap.to(orb, {
            x: `random(-40, 40)`, y: `random(-30, 30)`,
            duration: 6 + i * 2, repeat: -1, yoyo: true, ease: 'sine.inOut',
          });
        });
      }

      // Glow-on-reveal
      document.querySelectorAll('.v5l-glow').forEach((el) => {
        gsap.fromTo(el, { boxShadow: '0 0 0px transparent', y: 30, opacity: 0 }, {
          boxShadow: `0 0 40px ${TEAL}15, 0 0 80px ${TEAL}08`,
          y: 0, opacity: 1, duration: 0.8, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 80%' },
        });
      });

      // Text reveals
      document.querySelectorAll('.v5l-reveal').forEach((el) => {
        gsap.fromTo(el, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7,
          scrollTrigger: { trigger: el, start: 'top 82%' },
        });
      });

      // Screenshot luminous entrance
      gsap.fromTo('.v5l-shot', { y: 60, opacity: 0, scale: 0.96 }, {
        y: 0, opacity: 1, scale: 1, duration: 1, ease: 'power2.out',
        scrollTrigger: { trigger: '.v5l-shot', start: 'top 80%' },
      });
    });
    return () => ctx.revert();
  }, []);

  const features = [
    { icon: FolderOpen, title: 'Asset Library', desc: 'Every brand element, organized and tagged.', glow: TEAL },
    { icon: Users, title: 'Entity Profiles', desc: 'Artists and venues with linked assets.', glow: VIOLET },
    { icon: Sparkles, title: 'AI Generation', desc: 'Context-aware, brand-grounded flyers.', glow: CORAL },
    { icon: Shield, title: 'Brand Lock', desc: 'Identity enforced, automatically.', glow: TEAL },
    { icon: Zap, title: 'Instant Output', desc: 'Print-ready in seconds.', glow: CORAL },
    { icon: Wand2, title: 'Style Presets', desc: 'Curated starting points for every vibe.', glow: VIOLET },
  ];

  return (
    <div style={{ fontFamily: SANS, background: '#080810', color: '#b8b8c8', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Animated mesh gradient */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-40" style={{
        background: `
          radial-gradient(ellipse at ${20 + Math.sin(scrollY * 0.001) * 10}% ${30 + Math.cos(scrollY * 0.001) * 10}%, ${TEAL}12 0%, transparent 50%),
          radial-gradient(ellipse at ${80 + Math.cos(scrollY * 0.0008) * 10}% ${60 + Math.sin(scrollY * 0.0008) * 10}%, ${CORAL}08 0%, transparent 50%),
          radial-gradient(ellipse at ${50 + Math.sin(scrollY * 0.0012) * 15}% ${80 + Math.cos(scrollY * 0.0012) * 10}%, ${VIOLET}08 0%, transparent 50%)
        `,
        transition: 'background 0.1s ease',
      }} />

      {/* Floating light orbs */}
      <div ref={orbsRef} className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
        <div className="absolute top-[20%] left-[15%] w-[300px] h-[300px] rounded-full" style={{ background: `radial-gradient(circle, ${TEAL}08, transparent 70%)`, filter: 'blur(40px)' }} />
        <div className="absolute top-[50%] right-[10%] w-[250px] h-[250px] rounded-full" style={{ background: `radial-gradient(circle, ${CORAL}06, transparent 70%)`, filter: 'blur(50px)' }} />
        <div className="absolute bottom-[20%] left-[40%] w-[350px] h-[350px] rounded-full" style={{ background: `radial-gradient(circle, ${VIOLET}05, transparent 70%)`, filter: 'blur(60px)' }} />
      </div>

      {/* Grain */}
      <div className="fixed inset-0 pointer-events-none z-[9998] opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ backdropFilter: 'blur(16px)', background: 'rgba(8,8,16,0.6)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-[1300px] mx-auto px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src="/eventtools-logo.svg" alt="" className="w-8 h-8" />
            <span style={{ fontFamily: SERIF, fontSize: 20, color: '#fff' }}>Event.Tools</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[13px]" style={{ color: '#666' }}>Features</a>
            <a href="#workflow" className="text-[13px]" style={{ color: '#666' }}>Workflow</a>
          </div>
          <Link to="/signin" className="px-5 py-2 text-[13px] font-medium rounded-full" style={{
            background: `linear-gradient(135deg, ${TEAL}, ${VIOLET})`,
            color: '#fff',
          }}>
            Get access
          </Link>
        </div>
      </nav>

      {/* Hero — gradient text */}
      <section className="relative min-h-screen flex flex-col justify-center items-center text-center px-8 pt-20">
        <div className="relative z-10">
          <div className="v5l-hero inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(10px)',
          }}>
            <Sparkles size={14} style={{ color: TEAL }} />
            <span className="text-xs font-medium" style={{ color: '#888' }}>AI-Powered Flyer Builder</span>
          </div>
          <h1 className="v5l-hero" style={{
            fontFamily: SERIF, fontSize: 'clamp(48px, 8vw, 100px)', fontWeight: 400,
            lineHeight: 1.0, letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, #fff 0%, ${TEAL} 40%, ${VIOLET} 70%, ${CORAL} 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundSize: '200% 200%',
            animation: 'gradientShift 6s ease infinite',
          }}>
            Your assets,<br />always ready.
          </h1>
          <p className="v5l-hero mt-6 max-w-lg mx-auto text-[15px] leading-relaxed" style={{ color: '#777' }}>
            The flyer builder that organizes every logo, headshot, and brand element — so AI generation always uses the right context.
          </p>
          <div className="v5l-hero mt-8 flex items-center justify-center gap-4">
            <Link to="/signin" className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-full" style={{
              background: `linear-gradient(135deg, ${TEAL}, ${VIOLET})`,
              color: '#fff',
              boxShadow: `0 0 30px ${TEAL}30, 0 0 60px ${VIOLET}15`,
            }}>
              Get early access <ArrowRight size={16} />
            </Link>
            <a href="#features" className="px-7 py-3.5 text-sm rounded-full" style={{
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#888',
              backdropFilter: 'blur(10px)',
            }}>
              Explore
            </a>
          </div>
        </div>

        {/* Hero screenshot with glow */}
        <div className="v5l-hero relative mt-16 max-w-5xl w-full">
          <div className="absolute -inset-4 rounded-2xl" style={{
            background: `linear-gradient(135deg, ${TEAL}20, ${VIOLET}15, ${CORAL}10)`,
            filter: 'blur(30px)',
          }} />
          <div className="relative rounded-2xl overflow-hidden" style={{
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: `0 0 80px ${TEAL}10`,
          }}>
            <img src="/screenshot-library.png" alt="Event.Tools" className="w-full" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(8,8,16,0.6) 0%, transparent 40%)' }} />
          </div>
        </div>
      </section>

      {/* Features — glowing glass cards */}
      <section id="features" className="py-24 px-8">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14 v5l-reveal">
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(32px, 4vw, 52px)', color: '#eee', letterSpacing: '-0.02em' }}>
              Everything you need.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} className="v5l-glow group relative p-6 rounded-xl overflow-hidden" style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${f.glow}15`,
                backdropFilter: 'blur(10px)',
              }}>
                {/* Glow spot */}
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{ background: `radial-gradient(circle, ${f.glow}15, transparent 70%)` }} />
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.glow}10` }}>
                    <f.icon size={20} style={{ color: f.glow }} />
                  </div>
                  <h3 className="text-[15px] font-semibold mb-1.5" style={{ color: '#eee' }}>{f.title}</h3>
                  <p className="text-[13px] leading-relaxed" style={{ color: '#666' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product + text — asymmetric */}
      <section className="py-20 px-8 v5l-reveal">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <h2 className="mb-4" style={{ fontFamily: SERIF, fontSize: 'clamp(28px, 3.5vw, 44px)', color: '#eee', letterSpacing: '-0.02em' }}>
              AI that uses<br /><em style={{ color: TEAL }}>your brand.</em>
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color: '#777' }}>
              Unlike generic AI, Event.Tools generates flyers with your real logos, headshots, and brand identity. Every artist and venue has a profile.
            </p>
            <div className="space-y-3">
              {['Artist logos from profiles', 'Venue colors applied', 'Lineups auto-linked'].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: [TEAL, VIOLET, CORAL][i] }} />
                  <span className="text-sm" style={{ color: '#888' }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="v5l-shot relative rounded-xl overflow-hidden" style={{
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: `0 0 60px ${VIOLET}08`,
            }}>
              <img src="/screenshot-events.png" alt="" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="py-24 px-8 v5l-reveal">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="mb-12" style={{ fontFamily: SERIF, fontSize: 'clamp(28px, 3.5vw, 44px)', color: '#eee', letterSpacing: '-0.02em' }}>
            Three steps. <em style={{ color: TEAL }}>That's it.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: '01', t: 'Upload', d: 'Add your brand assets.', c: TEAL },
              { n: '02', t: 'Organize', d: 'Link to artists & venues.', c: VIOLET },
              { n: '03', t: 'Generate', d: 'One click. Done.', c: CORAL },
            ].map((s, i) => (
              <div key={i} className="v5l-glow p-6 rounded-xl" style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${s.c}15`,
                backdropFilter: 'blur(10px)',
              }}>
                <span style={{ fontFamily: SERIF, fontSize: 36, color: `${s.c}30` }}>{s.n}</span>
                <h3 className="text-base font-semibold mb-1 mt-2" style={{ color: '#eee' }}>{s.t}</h3>
                <p className="text-xs" style={{ color: '#666' }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo with luminous overlay */}
      <section className="relative h-[50vh] overflow-hidden v5l-reveal">
        <img src="/images/distribute_dj_lasers.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.3) saturate(1.4)' }} />
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${TEAL}15, transparent 40%, ${VIOLET}10 70%, ${CORAL}08)`,
        }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #080810, transparent 30%), linear-gradient(to bottom, #080810, transparent 30%)' }} />
        <div className="relative z-10 h-full flex items-center justify-center px-6">
          <h2 className="text-center" style={{
            fontFamily: SERIF, fontSize: 'clamp(28px, 5vw, 60px)', color: '#fff', letterSpacing: '-0.02em',
          }}>
            Built for <em style={{ color: TEAL }}>the night.</em>
          </h2>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-8 text-center v5l-reveal">
        <h2 className="mb-4" style={{
          fontFamily: SERIF, fontSize: 'clamp(36px, 5vw, 64px)', letterSpacing: '-0.02em',
          background: `linear-gradient(135deg, #fff, ${TEAL} 50%, ${VIOLET})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          Ready?
        </h2>
        <p className="mb-10 max-w-sm mx-auto text-sm" style={{ color: '#666' }}>Join early access. Never hunt for assets again.</p>
        <Link to="/signin" className="inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold rounded-full" style={{
          background: `linear-gradient(135deg, ${TEAL}, ${VIOLET})`,
          color: '#fff',
          boxShadow: `0 0 40px ${TEAL}25, 0 0 80px ${VIOLET}10`,
        }}>
          Get early access <ArrowRight size={16} />
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-10 px-8" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-[1300px] mx-auto flex items-center justify-between">
          <span style={{ fontFamily: SERIF, color: '#555', fontSize: 15 }}>Event.Tools</span>
          <span className="text-[11px]" style={{ color: '#333' }}>© {new Date().getFullYear()}</span>
        </div>
      </footer>

      {/* Gradient animation keyframe */}
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
}
